// WorkspaceSubscriptionWriteService.js

import { functions, httpsCallable } from 'firebaseConfig';

const callable = (name) => httpsCallable(functions, name);

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${name} must be a non-empty string`);
  }
}

/**
 * Creates a Stripe setup intent for a workspace billing customer.
 *
 * @param {object} params - Setup intent input.
 * @param {string} params.workspaceKey - Workspace key.
 * @param {string} params.email - Billing email.
 * @param {string} params.name - Billing name.
 *
 * @returns {Promise<object>} Setup intent response.
 */
export async function createWorkspaceSetupIntent({ workspaceKey, email, name }) {
  assertNonEmptyString(workspaceKey, 'workspaceKey');
  assertNonEmptyString(email, 'email');
  assertNonEmptyString(name, 'name');

  const fn = callable('createSetupIntent');
  const { data } = await fn({
    email,
    name,
    workspaceKey,
  });

  return data;
}

/**
 * Creates a Stripe subscription for a workspace.
 *
 * @param {object} params - Subscription input.
 * @param {string} params.workspaceKey - Workspace key.
 * @param {string} params.email - Billing email.
 * @param {string} params.name - Billing name.
 * @param {string} params.planId - Stripe Price ID.
 * @param {string} params.planKey - Internal plan key.
 * @param {string} params.paymentMethodId - Stripe payment method ID.
 * @param {string} [params.userKey] - Optional requester user key for audit metadata.
 *
 * @returns {Promise<object>} Subscription response.
 */
export async function createWorkspaceStripeSubscription({
  workspaceKey,
  email,
  name,
  planId,
  planKey,
  paymentMethodId,
  userKey = '',
}) {
  assertNonEmptyString(workspaceKey, 'workspaceKey');
  assertNonEmptyString(email, 'email');
  assertNonEmptyString(name, 'name');
  assertNonEmptyString(planId, 'planId');
  assertNonEmptyString(planKey, 'planKey');
  assertNonEmptyString(paymentMethodId, 'paymentMethodId');

  const fn = callable('createStripeSubscription');
  const { data } = await fn({
    email,
    name,
    paymentMethodId,
    planId,
    planKey,
    userKey: String(userKey || '').trim(),
    workspaceKey,
  });

  return data;
}

/**
 * Cancels a Stripe subscription for a workspace.
 *
 * @param {object} params - Cancellation input.
 * @param {string} params.workspaceKey - Workspace key.
 * @param {string} [params.subscriptionId] - Optional Stripe subscription ID.
 *
 * @returns {Promise<object>} Cancellation response.
 */
export async function cancelWorkspaceStripeSubscription({
  workspaceKey,
  subscriptionId = '',
}) {
  assertNonEmptyString(workspaceKey, 'workspaceKey');

  const fn = callable('cancelStripeSubscription');
  const { data } = await fn({
    subscriptionId: String(subscriptionId || '').trim(),
    workspaceKey,
  });

  return data;
}

/**
 * Creates a Stripe billing portal session for a workspace billing customer.
 *
 * @param {object} params - Billing portal input.
 * @param {string} params.workspaceKey - Workspace key.
 * @param {string} [params.returnUrl] - Optional absolute URL to return to after portal flow.
 *
 * @returns {Promise<object>} Billing portal session response.
 */
export async function createWorkspaceBillingPortalSession({
  workspaceKey,
  returnUrl = '',
}) {
  assertNonEmptyString(workspaceKey, 'workspaceKey');

  const fn = callable('createWorkspaceBillingPortalSession');
  const { data } = await fn({
    returnUrl: String(returnUrl || '').trim(),
    workspaceKey,
  });

  return data;
}
